const en = {
  nav: {
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    profile: 'Profile',
    accountMenu: 'Account menu',
    home: 'Home',
    portfolio: 'Portfolio',
    modeling: 'Modeling',
    // Nav label is the verb "Upload" (the action you come here to do); the route,
    // page title, and H1 stay plural "Uploads".
    uploads: 'Upload',
    settings: 'Settings',
    toggleMenu: 'Toggle navigation menu',
  },
  portfolio: {
    title: 'Portfolio',
    subtitle: 'Every loan in your uploaded portfolio, with modeling readiness at a glance.',
    tableLabel: 'Portfolio loans',
    loadError: 'We could not load the portfolio. Please try again.',
    // Quiet indicator while a large upload/delete's projection is still
    // recomputing (the dirty-loan queue is draining). Pluralized by count.
    updating_one: 'Updating {{count, number}} loan…',
    updating_other: 'Updating {{count, number}} loans…',
    columns: {
      loan: 'Loan',
      originalAmount: 'Original Amount',
      origination: 'Origination',
      fico: 'FICO',
      rate: 'Rate',
      currentBalance: 'Current Balance',
      snapshots: 'Snapshots',
      transactions: 'Transactions',
      readiness: 'Readiness',
      taxIdOnFile: 'Tax ID on file',
      status: 'Status',
      originalBalance: 'Original Balance',
      term: 'Term',
      age: 'Age',
      modelable: 'Modelable',
    },
    terminalStatus: {
      none: 'Active',
      paid_off: 'Paid off',
      charged_off: 'Charged off',
    },
    termSource: {
      provided: 'Provided',
      derived_from_maturity: 'Derived',
      none: 'None',
    },
    // F184: Original Balance provenance subtitle (mirrors termSource).
    originalBalanceSource: {
      provided: 'Provided',
      estimated: 'Est. (first balance)',
    },
    months: '{{count}} mo',
    modelable: {
      yes: 'Yes',
      no: 'No',
    },
    readiness: {
      creditLoss: 'Credit loss',
      payoff: 'Payoff',
      prepayment: 'Prepayment',
      ready: 'Ready',
      notReady: 'Not ready',
      order: 'Credit loss · Payoff · Prepayment',
    },
    stats: {
      totalLoans: 'Total Loans',
      snapshots: 'Snapshots',
      transactions: 'Transactions',
      creditLossReady: 'Credit-Loss Ready',
      payoffReady: 'Payoff Ready',
      prepaymentReady: 'Prepayment Ready',
      ofLoans: '{{percent}}% of loans',
    },
    toolbar: {
      // F124: the search runs `ilike` on the HMAC'd protectedLoanId, so a raw core-system loan
      // number can never match — label it for the scrambled identifier actually shown in the table.
      searchPlaceholder: 'Search by protected ID',
      searchLabel: 'Search loans by protected ID',
      searchHelper: 'IDs are scrambled for privacy — search the protected identifier shown in the table.',
      clear: 'Clear',
    },
    filters: {
      creditLossReady: 'Credit-loss ready',
      payoffReady: 'Payoff ready',
      prepaymentReady: 'Prepayment ready',
      hasTransactions: 'Has transactions',
    },
    empty: {
      noMatchesTitle: 'No loans match your filters',
      noMatchesBody: 'Try a different search, or clear the filters to see more loans.',
      noDataTitle: 'No portfolio yet',
      noDataBody:
        'Upload loan data to build your portfolio. Accepted uploads appear here as modeled loans.',
      upload: 'Upload loan data',
    },
  },
  modeling: {
    title: 'Modeling',
    subtitle:
      'Cohort analytics for the loans in your active segment, by loan age. Every number is hand-re-derivable, no black box.',
    // Two-part recalc overlay shown while a changed segment is being re-modeled.
    recalc: {
      processing: 'Processing Segment',
      analyzing: 'Running Analysis',
    },
    // Block-until-settled state: shown FULL on both the Modeling and Portfolio screens while the
    // stored modeling dataset is still being computed (loans deriving, or a refresh pending), so
    // neither screen shows confident numbers off a partially-derived portfolio. The count is the
    // loans still to process; pluralized. No numbers are shown until this clears.
    processing: {
      title: 'Still processing your portfolio',
      // Pluralized by the count of loans still being processed.
      remaining_one: '{{count, number}} loan remaining. This view will unlock automatically.',
      remaining_other: '{{count, number}} loans remaining. This view will unlock automatically.',
      // Shown when we know work is in flight but not yet a specific count (e.g. a refresh pending).
      preparing: 'Preparing your analytics. This view will unlock automatically.',
    },
    // "Updating" banner shown ON the whole-portfolio Modeling view while it serves the last-complete
    // dataset during an in-flight refresh (real numbers now, not a blocking wall). N of M progress +
    // an as-of, so the customer is never misled that these are the very latest figures.
    updating: {
      title: 'Updating your analytics',
      // N of M loans re-derived so far; the numbers below reflect the last complete run.
      body: 'These numbers reflect your last complete run. A refresh is in progress ({{derived}} of {{total}} loans updated).',
      // Appended after the body when an as-of timestamp is available.
      asOf: 'Updated as of {{asOf}}.',
    },
    // CSV / XLSX download affordance on each chart and table.
    export: {
      download: 'Download',
      csv: 'CSV (data)',
      xlsx: 'Excel (data + chart)',
      error: 'Could not generate the download. Please try again.',
    },
    loadError: 'We could not load the modeling analytics. Please try again.',
    // KPI row — the headline numbers across the top of the screen.
    kpi: {
      expectedLifetimeLoss: 'Expected Lifetime Loss',
      expectedLife: 'Expected Life',
      avgAnnualizedPrepayment: 'Avg Annualized Prepayment',
      // Tooltip-style expansion of the prepayment KPI label.
      avgAnnualizedPrepaymentMeta: 'CPR (annualized)',
      loansUsed: 'Loans Used',
      totalBalance: 'Total Balance',
      // Inline text labels (shown as "Loans: 320" / "Total Balance: $30,027,456" above the cards).
      loansLabel: 'Loans:',
      totalBalanceLabel: 'Total Balance:',
      // Value formatters (units appended to the computed number).
      years_one: '{{count, number}} yr',
      years_other: '{{count, number}} yrs',
      // The contractual-life shown as meta under the expected-life KPI.
      contractualMeta: 'Contractual: {{years, number}} yrs',
      // Shown under the lifetime-loss KPI when no loss-magnitude field is mapped
      // anywhere — the em-dash value means "no data", never a measured 0%.
      noLossDataMeta: 'No charge-off data uploaded',
      // F174: shown under the CPR + expected-life KPIs when the speed pool is empty (no loan
      // has an observed scheduled-principal basis) — the em-dash means "not measurable", never
      // a confident 0.00% CPR / 0.0-year life.
      noPrepaymentDataMeta: 'No prepayment data uploaded',
    },
    // WS-G / R5: the always-visible Data-Basis Statement — the ONE consolidated disclosure
    // surface (measured / inferred / excluded + the DATA as-of + engine method). Its counts are
    // the reused per-slice notes below (modeling.notes.* / modeling.empty.*); these keys are the
    // block's own scaffolding.
    dataBasis: {
      label: 'Data basis',
      // The DATA as-of (F183): the reference-snapshot month the curves are right-censored to.
      asOf: 'Measured against snapshots through {{month}}',
      // Measured population + modeled-only balance (F181). modeled/total/balance are preformatted.
      measured: 'Modeling {{modeled}} of {{total}} loans — {{balance}} current balance.',
      // Subsection eyebrows.
      inferredTitle: 'Inferred (resting on an assumption)',
      excludedTitle: 'Excluded (and what would unlock them)',
      // Method badges beside the measured line.
      noPrepaymentBadge: 'No prepayment data',
      noLossBadge: 'No charge-off data',
      // Unlock nudges appended under an excluded reason whose base copy is terse. (noTerm /
      // noLossAmount reuse the shared uploader.parityExclusions.* family; this one covers the
      // no-origination-anchor case that family doesn't.)
      unlock: {
        noOriginationDate:
          'Provide an origination date — a two-column Loan ID + Origination Date file is enough — to age and model these loans.',
      },
    },
    // Sticky section tabs (jump to each analysis section).
    tabs: {
      creditLoss: 'Credit loss',
      prepayment: 'Prepayment',
      pricing: 'Pricing',
      results: 'Results',
    },
    // Section headings + plain-English captions.
    sections: {
      creditLoss: {
        title: 'Credit Loss',
        caption:
          'How much of the original balance this cohort has lost to charge-offs by each loan age, projected to term. Built by averaging the historical loss experience of older, similar loans.',
        // Headline expected-lifetime-loss number shown beside the curve.
        headlineLabel: 'Expected lifetime loss',
        headlineMeta: 'Cumulative loss at the contractual term.',
        // Chart labels (the cumulative-loss curve).
        chartAriaLabel: 'Cumulative credit loss by loan age',
        chartValueLabel: 'Cumulative loss',
        chartXAxis: 'Loan age (months)',
        chartYAxis: 'Cumulative loss (%)',
        // "What backs the tail" note under the curve: how many loans support the
        // last observed age, beyond which the curve is projected.
        tailNote:
          'Solid through {{months, number}} months, backed by {{count, number}} loans at that age; the dashed tail is projected to term.',
        tailNoteNoExtrapolation:
          'Observed through {{months, number}} months, backed by {{count, number}} loans at that age.',
        // Empty state: cohort has no measurable loss history to chart.
        emptyTitle: 'No loss history yet',
        emptyBody:
          'This cohort has no charge-offs recorded against an aged balance, so there is no loss curve to plot. The expected lifetime loss is zero on the data uploaded so far.',
        // No-data state: NO loss-magnitude field is mapped anywhere in the
        // portfolio, so the flat 0% is absence of data — not a clean book.
        noLossDataTitle: 'No charge-off data in your uploads',
        noLossDataBody:
          'None of your uploads include a charge-off amount, so credit loss can’t be measured — this is missing data, not a measured 0% loss rate. Upload charge-off amounts (in your snapshots or a separate transaction file) to model credit loss.',
      },
      prepayment: {
        title: 'Prepayment and Runoff',
        caption:
          'How quickly this cohort returns principal early, through payoffs and partial prepayments, compared with the contractual schedule. Expected life is the behavioral weighted-average life; contractual life assumes no early payoff.',
      },
      pricing: {
        title: 'Pricing',
        caption:
          'The note rate this cohort needs to cover funding, expected loss, operating costs, and your target return. Built up component by component so every basis point is visible.',
      },
      cashflow: {
        title: 'Cash Flow and Profitability',
        caption:
          'Month-by-month projected income for a representative loan in this cohort, after expected defaults and prepayments, with cumulative profitability and break-even.',
      },
    },
    // Chart provenance legend (shared across curves).
    legend: {
      // Engine-method badge on the credit-loss curve.
      methodVintage: 'Vintage method',
      methodWarm: 'WARM fallback',
    },
    // Note shown when a cohort is too thin at some ages to be reliable.
    thinCohort: 'Directional only, backed by {{count, number}} loans at this age.',
    // Informational per-slice notes (provenance, not warnings).
    notes: {
      // WS-C P6 (F74/F42): loans a PROCESSING failure quarantined — excluded from the served
      // curves (never silently served stale) and disclosed with the honesty voice: temporary,
      // ours to fix, distinct from the permanent business exclusions below.
      quarantinedExcluded_one:
        '{{count, number}} loan is temporarily excluded while we investigate a processing issue. Its numbers return automatically once resolved.',
      quarantinedExcluded_other:
        '{{count, number}} loans are temporarily excluded while we investigate a processing issue. Their numbers return automatically once resolved.',
      derivedTerm_one:
        'Term derived from the maturity date for {{count, number}} loan with no term field.',
      derivedTerm_other:
        'Term derived from the maturity date for {{count, number}} loans with no term field.',
      estimatedOrigination_one:
        '{{count, number}} loan is aged from an estimated origination (its first appearance in the data), not a provided origination date — its age and vintage rest on that estimate.',
      estimatedOrigination_other:
        '{{count, number}} loans are aged from an estimated origination (their first appearance in the data), not a provided origination date — their age and vintage rest on that estimate.',
      impliedPayoff_one:
        '{{count, number}} loan is treated as fully paid off because it stopped reporting before the latest snapshot — an inference, not a reported payoff, so it counts toward measured prepayment.',
      impliedPayoff_other:
        '{{count, number}} loans are treated as fully paid off because they stopped reporting before the latest snapshot — an inference, not a reported payoff, so they count toward measured prepayment.',
      seasonedEntrant_one:
        '{{count, number}} loan was originated before your earliest snapshot, so its life before that first snapshot is excluded from the curves — loans that ended before the window are invisible, and counting only the survivors would understate early-age risk.',
      seasonedEntrant_other:
        '{{count, number}} loans were originated before your earliest snapshot, so their life before that first snapshot is excluded from the curves — loans that ended before the window are invisible, and counting only the survivors would understate early-age risk.',
      // v8 (core-A, RB-4): the prepayment measure's denominator requires REPORTED Scheduled
      // Principal (due or paid) — never a schedule reconstructed from rate, payment, or
      // balance data. Loans without it are excluded from the measure entirely and counted.
      prepayNoSchedule_one:
        '{{count, number}} loan is excluded from the prepayment measure — it has no reported Scheduled Principal (due or paid), and the measure never reconstructs a schedule from rate, payment, or balance data. It still counts toward credit loss. Adding a Scheduled Principal field would include it.',
      prepayNoSchedule_other:
        '{{count, number}} loans are excluded from the prepayment measure — they have no reported Scheduled Principal (due or paid), and the measure never reconstructs a schedule from rate, payment, or balance data. They still count toward credit loss. Adding a Scheduled Principal field would include them.',
      // Speed-pool ruling (2026-07-17): a loan with reported Scheduled Principal is always in
      // the speed measure — full-payoff detection covers every loan, so a loan that never
      // paid off is a measured zero. What CAN'T be seen without a partial-reporting column is
      // partial prepayment — disclosed here, not excluded.
      prepayPartialUnreported_one:
        'Partial prepayment is not reported for {{count, number}} loan — its prepayment speeds reflect full payoffs only. Add a Partial Prepayment Amount or Actual + Scheduled Principal column to measure partial prepayment.',
      prepayPartialUnreported_other:
        'Partial prepayment is not reported for {{count, number}} loans — prepayment speeds for them reflect full payoffs only. Add a Partial Prepayment Amount or Actual + Scheduled Principal column to measure partial prepayment.',
      // v8 (core-A, RB-3): a reported prepayment amount on a month whose starting balance was
      // never observed (a reporting gap or the loan's first appearance mid-life) cannot be
      // measured as a speed — dropped from the measure and disclosed, never spread or guessed.
      prepayDroppedMonths_one:
        '{{count, number}} reported prepayment fell in a month whose starting balance was not in the data (a reporting gap or a first appearance), so it can’t be measured as a rate — it is set aside from the prepayment speeds, not spread or guessed.',
      prepayDroppedMonths_other:
        '{{count, number}} reported prepayments fell in months whose starting balances were not in the data (reporting gaps or first appearances), so they can’t be measured as rates — they are set aside from the prepayment speeds, not spread or guessed.',
      // Founder-directed (2026-07-17): loans whose exports still carry values after their
      // terminal charge-off. The curves treat a charge-off as terminal (one event, later
      // months excluded — F162), so a partial-write-down book should declare itself rather
      // than be silently mis-modeled.
      postChargeOffActivity_one:
        '{{count, number}} loan shows activity after its charge-off — its later months are excluded from the curves. If your loans can survive a partial charge-off, tell us so we can model them as alive.',
      postChargeOffActivity_other:
        '{{count, number}} loans show activity after their charge-off — their later months are excluded from the curves. If your loans can survive a partial charge-off, tell us so we can model them as alive.',
      // v9 (partial-CO): the VOCAB-book variant — the org's status vocabulary already
      // decides when a loan is charged off, so the note is informational (no ask): these
      // months sit after an explicit charged-off status/flag and stay excluded.
      postChargeOffActivityVocab_one:
        '{{count, number}} loan keeps reporting values after its charged-off status — those later months are excluded from the curves.',
      postChargeOffActivityVocab_other:
        '{{count, number}} loans keep reporting values after their charged-off status — those later months are excluded from the curves.',
      // v8 (WS-B P3 — F175): flag/status-only charge-offs with no derivable loss amount stay out
      // of the LOSS curve (rather than counting as $0 losses) but remain in survival/prepayment.
      lossExcludedFlagOnly_one:
        '{{count, number}} loan is marked charged-off by a flag or status only, with no loss amount anywhere in the data — it is excluded from the loss curve (counting it as a $0 loss would understate losses) but still counts toward survival and prepayment.',
      lossExcludedFlagOnly_other:
        '{{count, number}} loans are marked charged-off by a flag or status only, with no loss amount anywhere in the data — they are excluded from the loss curve (counting them as $0 losses would understate losses) but still count toward survival and prepayment.',
      // v8 (WS-B P4 — F150): fires only when the ENTIRE loss population entered the data
      // mid-life (the min per-loan observable-age floor is above zero) — ages below the floor
      // were never observable, so the loss curve cannot speak to them.
      firstObservableAge:
        'Loss observation for these loans begins at age {{months, number}} months — every loan entered the data at least that far into its life, so earlier ages can’t be measured for this slice.',
    },
    // v9 (partial-CO): the survivorship declared fact — the evidence-triggered question
    // (founder-ruled copy), asked only of books whose data demands it (post-charge-off
    // activity with no charged_off status vocabulary). Prefilled 'leaves'; never blocks.
    chargeOffSurvivorship: {
      question:
        'When a loan is charged off, does it leave your exports, or can it survive a partial write-down?',
      optionLeaves: 'It leaves our exports',
      optionSurvives: 'It can survive a partial write-down',
      suggestedHint:
        'Suggested default — most cores drop a loan at charge-off. If your loans can survive a partial write-down, choosing so re-models them as alive: each charge-off amount books as a loss in its own month and the loan keeps counting while it reports.',
      declaredSurvivesHint:
        'You’ve told us charged-off loans can survive a partial write-down — charge-off amounts book as losses in their own month, and each loan stays modeled while it keeps reporting.',
      declaredLeavesHint:
        'You’ve told us a charged-off loan leaves your exports — its first charge-off ends it, and any later reported months are excluded from the curves.',
    },
    // "This slice mixes loan terms" callout — the projected tail assumes a single term.
    termDispersion: {
      title: 'This slice mixes loan terms',
      body:
        'These loans span {{min, number}} to {{max, number}}-month terms (weighted average {{avg, number}}). The observed curves use each loan’s own term, but the projected tail assumes one term, so it may not represent any actual loan. Consider slicing by term for a cleaner projection.',
    },
    // Empty / unavailable states.
    empty: {
      noAgeableTitle: 'No loans can be aged yet',
      // v9 (WS-F / R2): the old copy ("needs an origination date") was imprecise — a loan
      // first appearing AFTER the earliest snapshot month ages without one. The rewrite
      // states the real rule and carries the nudge.
      noAgeableBody:
        'Modeling ages each loan from its origination. A loan seen only in your earliest snapshot month with no origination date can’t be aged — we can’t tell whether it’s new or seasoned. Add origination dates (a Loan ID + Origination Date file is enough) to bring these loans in.',
      noTermTitle: 'No loan terms yet',
      noTermBody:
        'Modeling needs a loan term to project a lifetime. None of the aged loans in this segment have a term, directly or via a maturity date, so there is nothing to project. Upload a term or maturity date to see curves.',
      // Surfaced count of loans dropped from the curves for lack of an origination date.
      excludedNote_one:
        '{{count, number}} loan excluded from the curves: no origination date.',
      excludedNote_other:
        '{{count, number}} loans excluded from the curves: no origination date.',
      // v9 (WS-F / R2): unanchored-first-month exclusions + the origination-date nudge. The
      // unlock count is the honest subset a date file ALONE unlocks (term already present).
      unanchoredFirstMonthNote_one:
        '{{count, number}} loan can’t be aged: it is seen only in your earliest snapshot month with no origination date, so we can’t tell a new loan from a seasoned one. It is excluded from the curves. Upload a two-column file (Loan ID + Origination Date) to place it and unlock {{unlockCount, number}} of these loans.',
      unanchoredFirstMonthNote_other:
        '{{count, number}} loans can’t be aged: they are seen only in your earliest snapshot month with no origination date, so we can’t tell a new loan from a seasoned one. They’re excluded from the curves. Upload a two-column file (Loan ID + Origination Date) to place them and unlock {{unlockCount, number}} of them.',
      // Variant when a date alone unlocks none of them (they also lack a term) — no unlock
      // promise the data can't keep.
      unanchoredFirstMonthNoteTermless_one:
        '{{count, number}} loan can’t be aged: it is seen only in your earliest snapshot month with no origination date, so we can’t tell a new loan from a seasoned one. It is excluded from the curves. Upload a two-column file (Loan ID + Origination Date) to place it — it will also need a term or maturity date to model.',
      unanchoredFirstMonthNoteTermless_other:
        '{{count, number}} loans can’t be aged: they are seen only in your earliest snapshot month with no origination date, so we can’t tell a new loan from a seasoned one. They’re excluded from the curves. Upload a two-column file (Loan ID + Origination Date) to place them — they will also need a term or maturity date to model.',
      // Surfaced count of aged loans dropped for lack of a term (direct or derivable from maturity).
      excludedNoTermNote_one:
        '{{count, number}} loan excluded from the curves: no loan term or maturity date.',
      excludedNoTermNote_other:
        '{{count, number}} loans excluded from the curves: no loan term or maturity date.',
      // v8 (WS-B P3): origination-extract loans — an origination anchor and a term but no
      // balance snapshot ever observed, so there is no age axis to place observations on.
      excludedNoSnapshotNote_one:
        '{{count, number}} loan excluded from the curves: no balance snapshot was ever observed for it. Upload a monthly snapshot covering this loan to include it.',
      excludedNoSnapshotNote_other:
        '{{count, number}} loans excluded from the curves: no balance snapshot was ever observed for them. Upload a monthly snapshot covering these loans to include them.',
      // Of the no-snapshot exclusions, how many carry a loss/payoff event that therefore cannot
      // be booked — surfaced so a real loss in the data is never silently absent from the curves.
      excludedNoSnapshotWithEventsNote_one:
        '{{count, number}} of those excluded loans has a charge-off or payoff on record that is not reflected in the curves — it needs a snapshot to be placed at a loan age.',
      excludedNoSnapshotWithEventsNote_other:
        '{{count, number}} of those excluded loans have charge-offs or payoffs on record that are not reflected in the curves — they need snapshots to be placed at a loan age.',
      noDataTitle: 'No portfolio yet',
      noDataBody:
        'Upload loan data to build your portfolio, then come back to model a segment of it.',
      upload: 'Upload loan data',
    },
    // Prepayment section — the survival curve, the CPR headline, life, and the
    // advanced SMM/CPR-by-age detail.
    prepayment: {
      // The remaining-balance survival chart.
      survivalTitle: 'Remaining balance by loan age',
      survivalAria: 'Remaining balance survival curve, behavioral versus contractual',
      survivalAxisY: 'Remaining balance',
      survivalAxisX: 'Loan age (months)',
      behavioral: 'Behavioral',
      contractual: 'Contractual',
      // Plain-English caption under the survival chart.
      survivalCaption:
        'The solid teal line is how fast this cohort actually pays down, including early payoffs and partial prepayments. The grey line is the contractual schedule with no early payoff. The gap between them is runoff.',
      // The CPR headline + the two life figures, shown as a small stat row.
      cprHeadline: 'Avg annualized prepayment',
      cprMeta: 'CPR (annualized)',
      expectedLife: 'Expected life',
      contractualLife: 'Contractual life',
      lifeMeta: 'Behavioral weighted-average life',
      contractualLifeMeta: 'Assuming no early payoff',
      // F174: the no-data meta shown under the CPR headline + both life cards when the
      // speed pool is empty (no observed scheduled-principal basis) — so a cohort with
      // no basis to measure reads "—", not a confident 0.00% CPR / 0.0-year life.
      noSpeedData: 'No prepayment basis — add scheduled-principal or payment columns',
      // Section meta: how many ageable loans back these curves.
      backedBy_one: '{{count, number}} loan',
      backedBy_other: '{{count, number}} loans',
      // Shown when the cohort produced no survival curve (too thin / no runoff).
      emptyCurve:
        'Not enough aged balance in this cohort to plot a survival curve yet. The figures above are still derived from what is observed.',
    },
    // Prepayment advanced view (reveals SMM/CPR-by-age for analysts/examiners).
    advanced: {
      toggle: 'Show SMM and CPR by age',
      caption:
        'The single-month mortality (SMM) and its annualized form (CPR) at each loan age, the per-period speeds behind the survival curve. Solid where backed by data, dashed where projected to term.',
      smm: 'SMM by age',
      cpr: 'CPR by age',
      smmAria: 'Single-month mortality by loan age',
      cprAria: 'Conditional prepayment rate by loan age',
      axisX: 'Loan age (months)',
      // Empty state when no per-age speeds were produced.
      empty: 'No per-age prepayment speeds for this cohort yet.',
    },
    // Pricing inputs + the required-rate build-up output.
    pricing: {
      inputsTitle: 'Assumptions',
      // Reset the assumptions back to their defaults.
      reset: 'Reset assumptions',
      proposedNoteRate: 'Proposed note rate',
      // Placeholder for the optional proposed-rate field (none entered = no delta).
      proposedNoteRatePlaceholder: 'Optional',
      targetSpread: 'Target spread over FTP',
      originationCost: 'Origination cost',
      annualServicingCost: 'Annual servicing cost',
      // The representative new loan being priced (amount + term, seeded from the cohort).
      loanAmount: 'Loan amount',
      // Guidance under the loan-amount field: the cohort's average loan size (preformatted, e.g. "$247,000").
      loanAmountSeed: 'Cohort average: {{amount}}',
      loanTerm: 'Term',
      contractualTermUnit: 'months',
      // Guidance under the term field: the cohort's balance-weighted average term.
      contractualTermSeed: 'Cohort average: {{months, number}} months',
      // Read-only display under the term input: the loan's expected life after the
      // cohort's measured prepayment (technically a weighted-average life).
      adjustedTerm: 'Adjusted term',
      adjustedTermValue: '~{{months, number}} months',
      // Compact form for the output card's life row.
      adjustedTermMonthsShort: '~{{months, number}} mo',
      adjustedTermHint:
        'Expected life of this loan after the cohort’s measured prepayment. The build-up prices the loan over this life; expected credit loss is read from the loss curve at the entered term.',
      upfrontFees: 'Upfront fees',
      recurringFees: 'Annual recurring fees',
      // Group headings within the assumptions form.
      groupLoan: 'Loan',
      groupMargin: 'Margin and costs',
      groupFees: 'Fees',
      // ── Vertical pricing subsections (FTP / Loan / Costs & Fees / Required return / Results) ──
      // FTP subsection: the editable grid (left) beside the CSV upload (right).
      ftpCurveEditorLabel: 'Enter your curve',
      ftpCurveUploadLabel: 'Or upload a CSV',
      // Loan subsection: the inputs (left) and their derived values (right).
      loanSectionDesc:
        'The representative new loan being priced, seeded from the cohort. Its derived values appear on the right.',
      proposedNoteRateDesc: 'Optional — the rate you plan to offer, compared against the required rate below.',
      calculatedLabel: 'Calculated for this loan',
      notYetAvailable: 'Provide a curve',
      adjustedTermHintShort: 'Expected life after the cohort’s measured prepayment.',
      expectedCreditLossLifetime: 'Expected credit loss (lifetime)',
      expectedCreditLossHint: 'Read from the loss curve at the entered term.',
      matchedFtpRate: 'Matched FTP rate',
      matchedFtpRateHint: 'Funding cost at the {{months, number}}-month expected life.',
      // Costs & fees subsection.
      groupCostsFees: 'Costs and fees',
      costsFeesDesc:
        'What it costs to make and service this loan, and any fees you collect. Costs raise the required rate; fees lower it.',
      groupCosts: 'Costs',
      // Custom line item (two-toggle layout): a name field + one-time/yearly, an amount + %/$.
      customCostLabel: 'Custom cost',
      customFeeLabel: 'Custom fee',
      lineItemNamePlaceholder: 'e.g. Dealer flat',
      amountFieldLabel: 'Amount',
      timing: {
        oneTime: 'One-time',
        yearly: 'Yearly',
      },
      addCustomCost: 'Add cost',
      addCustomFee: 'Add fee',
      // Required return subsection (ROA / ROE).
      groupReturn: 'Required return',
      returnDesc:
        'The profit this loan must earn on top of its costs. Enter it as a return on assets, or derive it from a target return on equity and the capital you hold against the loan.',
      returnBasisRoa: 'Target ROA',
      returnBasisRoe: 'Target ROE',
      targetRoa: 'Target return on assets (ROA)',
      targetRoaHint: 'The pre-tax return on the loan balance you need this loan to earn (around 1% is typical).',
      capitalAllocationHint: 'Share of the loan you hold as capital (equity).',
      targetPretaxRoeHint: 'The pre-tax return you require on that capital.',
      impliedRoa: 'Implied ROA',
      impliedRoaHint: 'Capital allocation times target ROE.',
      // Results subsection (the consolidated payoff).
      resultsTitle: 'Results',
      resultsDesc:
        'Everything from this pricing exercise in one place: the rate to charge, how your proposed rate compares, what the loan earns, and why.',
      pricingSummary:
        'Pricing a {{amount}} loan over {{term, number}} months (~{{life, number}} yr expected life).',
      profitabilityLabel: 'Profitability of this loan',
      buildupLabel: 'Rate build-up',
      // $/% entry-mode toggle labels (the small segmented control on convertible
      // inputs; custom line items add the per-year dollar basis).
      amountMode: {
        usd: '$',
        pct: '%',
        usdOneTime: '$',
        usdAnnual: '$/yr',
        pctAnnual: '%',
      },
      // User-added build-up line items (added under costs = adds; under fees = subtracts).
      addLineItem: 'Add line item',
      lineItemLabel: 'Line item label',
      lineItemLabelPlaceholder: 'e.g. Overhead allocation',
      lineItemValueLabel: 'Line item value',
      removeLineItem: 'Remove line item',
      // FTP curve block: the manual two-column editor + the CSV bulk-fill. There is
      // deliberately NO default curve — outputs gate until the user provides one.
      ftpCurveTitle: 'Funding (FTP) curve',
      ftpCurveSample: 'Download sample',
      ftpCurveManualHint:
        'Enter the rate your treasury charges for funds at each term, or upload a CSV below.',
      ftpCurveHint: 'CSV with term (months) and rate columns. We detect the layout for you.',
      ftpCurveParsing: 'Reading your curve…',
      ftpCurveDropPrompt: 'Drop a CSV here, or browse',
      // Manual editor controls (column headers reuse the term/rate labels below).
      ftpEditorTermAria: 'Term in months',
      ftpEditorRateAria: 'Rate in percent',
      ftpEditorAddPoint: 'Add point',
      ftpEditorRemovePoint: 'Remove point',
      // The FTP read at the loan's expected life (what the engine prices with).
      ftpCurveAtLife: 'FTP at expected life ({{months, number}} mo): {{rate}}',
      ftpCurveColTerm: 'Term (mo)',
      ftpCurveColRate: 'Rate',
      // Output gate shown in place of results until a funding curve is provided.
      ftpGateTitle: 'Provide your funding (FTP) curve to see results',
      ftpGateBodyPricing:
        'The required rate starts from your institution’s cost of funds, so there is no sensible default. Enter your funding curve in the assumptions — type it in or upload a CSV — and the build-up will appear here.',
      ftpGateBodyCashflow:
        'The projection charges funding costs off your institution’s funding (FTP) curve, so there is no sensible default. Enter the curve in the Pricing assumptions above — type it in or upload a CSV — to see the month-by-month cash flows.',
      // F113: gate shown in place of results when there is no positive loan amount (the
      // field is cleared and the cohort has no seed, or a $0 was entered) — the results
      // price one representative loan, so a $0 loan would drop the %-mode legs while a
      // confident rate still rendered.
      amountGateTitle: 'Enter a loan amount to see results',
      amountGateBody:
        'The results price one representative new loan, so they need a loan amount. Enter the loan amount in the assumptions above to see the required rate, profitability, and the month-by-month cash flows.',
      // Curve problems surfaced from validateFundingCurve (kept, but flagged).
      ftpCurveProblemsTitle: 'Check your funding curve',
      // Hard failure: the file had no usable rows at all.
      ftpCurveEmptyError:
        'No usable term and rate rows were found in that file. Each row needs a term in months and a rate in percent.',
      ftpCurveParseError:
        'We could not read that file as CSV. Check it is a plain .csv export.',
      // The file read fine but we could not identify a term and a rate column.
      ftpCurveColumnsError:
        'We could not find a term column and a rate column in that file. Include a term (months) column and a rate column. A header like "term_months, rate_pct" works best.',
      // RAROC capital path.
      rarocToggle: 'Use RAROC capital charge',
      rarocHint:
        'Replaces the target spread with a capital charge: capital allocation times target pre-tax ROE.',
      capitalAllocation: 'Capital allocation',
      targetPretaxRoe: 'Target pre-tax ROE',
      // Build-up component labels (the engine returns these keys per component).
      component: {
        ftp: 'Funding cost (FTP)',
        targetSpread: 'Target spread',
        // The residual return leg, at the bottom of the build-up.
        targetReturn: 'Required return (ROA)',
        capitalCharge: 'Required return (capital charge)',
        expectedCreditLoss: 'Expected credit loss',
        operatingCost: 'Operating cost',
        // Operating cost split out for auditability in the Results build-up.
        originationCost: 'Origination cost (amortized)',
        servicingCost: 'Servicing cost',
        feeIncome: 'Fee income credit',
        // Fallback label for a user-added line item with no label typed yet.
        custom: 'Custom line item',
      },
      // Outputs.
      outputsTitle: 'Required rate build-up',
      // One-line explainer above the waterfall.
      buildupCaption:
        'Each component adds to (or subtracts from) the funding cost to reach the rate this cohort needs. Every basis point is auditable.',
      requiredRate: 'Required rate',
      // Sub-caption under the required-rate headline (which maturity FTP is indexed at).
      requiredRateMeta: 'Indexed to a {{months, number}}-month funding cost',
      impliedSpread: 'Implied spread',
      impliedSpreadMeta: 'Required rate over FTP',
      // Expected vs contractual life shown beside the headline.
      expectedLife: 'Expected life',
      contractualLife: 'Contractual life',
      years_one: '{{count, number}} yr',
      years_other: '{{count, number}} yrs',
      // The total row of the waterfall.
      total: 'Required rate',
      // Underpricing delta vs the proposed note rate.
      underpricing: 'Versus proposed rate',
      underpriced: '{{bps, number}} bps underpriced',
      overpriced: '{{bps, number}} bps of cushion',
      onTarget: 'Priced on target',
      // Shown in place of the delta when no proposed rate has been entered.
      underpricingHint: 'Enter a proposed note rate to see the gap.',
    },
    // Cash-flow outputs.
    cashflow: {
      // Header of the month-by-month detail block inside Results.
      detailTitle: 'Month-by-month detail',
      monthlyNetCashFlow: 'Monthly net cash flow',
      cumulativeProfitability: 'Cumulative profitability',
      breakEvenMonth: 'Break-even month',
      breakEvenNever: 'Does not break even',
      remainingBalance: 'Remaining balance',
      lifetimeProfit: 'Lifetime profit',
      roa: 'Return on assets',
      nim: 'Net interest margin',
      npvAtFtp: 'NPV at FTP',
      tableTitle: 'Month-by-month projection',
      tableToggle: 'Show month-by-month table',
      tableHide: 'Hide month-by-month table',
      // Caption under the monthly net-cash-flow chart, explaining what is plotted.
      cashflowCaption:
        'Projected net income each month for a representative {{balance}} loan in this cohort, after expected defaults and prepayments. The teal line is cumulative profitability; it crosses zero at break-even.',
      // Caption under the remaining-balance chart.
      balanceCaption:
        'How the representative loan pays down over time, including expected defaults and prepayments, not just the contractual schedule.',
      // Sub-chart titles.
      remainingBalanceTitle: 'Remaining balance',
      // Metas under the headline cards.
      breakEvenMonthMeta: 'First month cumulative profit is positive',
      // Break-even card value when the loan recovers, e.g. "Month 14".
      breakEvenMonthValue: 'Month {{month, number}}',
      roaMeta: 'Net income / average balance',
      nimMeta: 'Interest less funding / average balance',
      npvAtFtpMeta: 'Discounted at the matched-maturity FTP',
      lifetimeProfitMeta: 'Total net income over the projection',
      // Note clarifying the rate used to drive the projection.
      rateNote: 'Projected at a {{rate}} note rate. Set a proposed rate in Pricing to model your own.',
      rateNoteRequired: 'Projected at the required rate of {{rate}} (no proposed rate set).',
      // Honest empty state when the projection cannot run.
      emptyTitle: 'Nothing to project yet',
      emptyBody:
        'A cash-flow projection needs a starting balance, a term, and the cohort runoff curves. This cohort does not have enough to project. Refine your segment or upload more loan history.',
      columns: {
        month: 'Month',
        beginBalance: 'Begin balance',
        interest: 'Interest',
        scheduledPrincipal: 'Scheduled principal',
        defaultedPrincipal: 'Defaulted principal',
        prepaidPrincipal: 'Prepaid principal',
        endBalance: 'End balance',
        fundingCost: 'Funding cost',
        servicingCost: 'Servicing cost',
        origCostAmort: 'Origination cost',
        loss: 'Loss',
        feeIncome: 'Fee income',
        // User-added custom line items, shown signed (income − costs); the column
        // only renders when the user has added line items.
        lineItems: 'Line items',
        netIncome: 'Net income',
        cumulativeNetIncome: 'Cumulative net income',
      },
    },
  },
  shell: {
    // Accessible name for the mobile nav drawer (the desktop rail's <nav> carries
    // its own label). No visible "Main" eyebrow exists anymore — the rail's single
    // primary group needs no heading.
    mobileNavLabel: 'Navigation',
    themeToggle: 'Toggle color scheme',
    // aria-label for the docked TopBar back button (published via usePageChrome).
    back: 'Back',
  },
  auth: {
    // Left value-props panel.
    // Hero statement — rendered as two intentional lines (the break is forced
    // after the comma, not left to wrapping).
    valuePropLine1: 'Use your own data,',
    valuePropLine2: 'not black box models.',
    // The three outcomes, shown as gilt-outlined chips under the statement.
    metricCreditLoss: 'Credit loss',
    metricPrepaymentRisk: 'Prepayment risk',
    metricNetInterestMargin: 'Net interest margin',
    valuePropSimple: 'Simple',
    valuePropTransparent: 'Transparent',
    valuePropAccurate: 'Accurate',
    // Right auth panel — sign-in variant.
    signInTitle: 'Welcome back',
    signInSubtitle:
      'Sign in to your account.',
    // Right auth panel — sign-up variant.
    signUpTitle: 'Create your account',
    signUpSubtitle:
      'Get started with {{appName}}.',
    // Credential form.
    nameLabel: 'Name',
    namePlaceholder: 'Ada Lovelace',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    passwordLabel: 'Password',
    passwordHint: 'At least 8 characters.',
    signInButton: 'Sign in',
    signUpButton: 'Create account',
    // Cross-link prompts between the two variants.
    noAccountPrompt: "Don't have an account?",
    hasAccountPrompt: 'Already have an account?',
    // Inline validation + submit errors.
    nameRequired: 'Please enter your name.',
    emailRequired: 'Please enter your email address.',
    emailInvalid: 'Please enter a valid email address.',
    passwordRequired: 'Please enter your password.',
    passwordTooShort: 'Password must be at least 8 characters.',
    signInError: 'Could not sign in. Check your email and password and try again.',
    signUpError: 'Could not create your account. Please try again.',
    // Duplicate-email sign-up: a specific message + a "sign in instead" link.
    emailAlreadyExists: 'An account already exists with this email.',
    signInInstead: 'Sign in instead',
    signInWithGoogle: 'Continue with Google',
    googleSignInError: 'Could not sign in with Google. Please try again.',
    // Forgot / reset password flow.
    forgotPassword: 'Forgot password?',
    backToSignIn: 'Back to sign in',
    forgotPasswordTitle: 'Reset your password',
    forgotPasswordSubtitle:
      "Enter your email and we'll send you a link to set a new password.",
    forgotPasswordSubmit: 'Send reset link',
    forgotPasswordSentTitle: 'Check your email',
    forgotPasswordSentSubtitle: 'If that address has an account, a reset link is on its way.',
    forgotPasswordSentBody:
      "We've sent a password reset link to {{email}} if an account exists. The link expires in one hour.",
    resetPasswordTitle: 'Choose a new password',
    resetPasswordSubtitle: 'Set a new password for your account.',
    resetPasswordSubmit: 'Reset password',
    resetPasswordError: 'Could not reset your password. Please try again.',
    resetPasswordInvalidToken:
      'This reset link is invalid or has expired. Request a new one to continue.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    passwordsMismatch: "Passwords don't match.",
  },
  onboarding: {
    // Shared onboarding shell (the Ink aside): brand orientation + step ledger.
    shell: {
      eyebrow: 'Getting started',
      headline: "You're almost in.",
      // Compact mobile band, e.g. "Step 1 of 2 · Verify your email".
      stepOf: 'Step {{current}} of {{total}} · {{label}}',
    },
    progress: {
      ariaLabel: 'Onboarding progress',
      verifyLabel: 'Verify your email',
      verifyHint: "Confirm it's really you",
      orgLabel: 'Name your organization',
      orgHint: 'Set up your workspace',
    },
    verifyEmail: {
      title: 'Verify your email',
      // Rendered as "{lead} you@company.com" with the address emphasised.
      subtitleLead: 'We sent a 6-digit code to',
      verify: 'Verify',
      resendPrompt: "Didn't get the code?",
      resend: 'Resend code',
      resendIn: 'Resend in {{seconds}}s',
      error: 'That code is incorrect or has expired. Try again.',
      illustrationAlt: 'An envelope carrying your verification code',
    },
    createOrg: {
      title: 'Create your organization',
      subtitle: 'Give your workspace a name. This is where your portfolio and team live.',
      nameLabel: 'Organization name',
      namePlaceholder: 'Acme Capital',
      hint: 'You can rename it anytime in Settings.',
      submit: 'Create organization',
      error: 'Could not create your organization. Please try again.',
    },
    // "You're all set" completion modal, shown once on the landing surface.
    success: {
      title: "You're all set",
      subtitle: 'Your workspace is ready. Welcome to {{appName}}.',
      cta: 'Get started',
      illustrationAlt: 'A checkmark confirming setup is complete',
    },
  },
  orgSwitcher: {
    eyebrow: 'Organization',
    menuLabel: 'Switch organization',
    settings: 'Org Settings',
    create: 'Create organization',
    createTitle: 'Create organization',
    createBody: 'Give your new organization a name. You can change it later in settings.',
    createNamePlaceholder: 'Organization name',
    createCancel: 'Cancel',
    createSubmit: 'Create',
    createError: 'We could not create the organization. Please try again.',
  },
  // Pending organization invitations: the onboarding "join your team" step
  // (org-less users) and the signed-in prompt (users who already have an org).
  invitations: {
    title: 'Join your team',
    subtitle:
      'You have pending invitations. Accept one to join that workspace, or decline to set up your own organization.',
    promptTitle: 'Pending invitations',
    promptBody:
      'You have been invited to join another organization. Accepting switches you into that workspace.',
    invitedByAs: 'Invited by {{name}} · {{role}}',
    joinAs: 'Join as {{role}}',
    accept: 'Accept',
    decline: 'Decline',
    notNow: 'Not now',
    acceptError: 'Could not accept the invitation. Please try again.',
    declineError: 'Could not decline the invitation. Please try again.',
  },
  unauthorized: {
    title: "You're not authorized to view this content.",
    body: "Your role doesn't include access to this page. Contact an organization admin if you need it.",
    back: 'Back to Modeling',
  },
  blocked: {
    title: "Your IP address isn't allowed",
    body: 'This organization restricts access to a list of approved IP addresses, and your current one is not on it.',
    bodyWithOrg:
      '{{name}} restricts access to a list of approved IP addresses, and your current one is not on it.',
    yourIpLabel: 'Your IP address',
    ipUnknown: 'Unknown',
    switchOrgLabel: 'Switch to another organization',
    switchOrgPlaceholder: 'Choose an organization…',
    currentOrgOption: '{{name}} (current)',
    signOut: 'Log out',
    supportPrefix: 'If you think this is a mistake, reach out to',
  },
  settings: {
    title: 'Settings',
    nav: {
      organization: 'Organization',
      profile: 'Profile',
      team: 'Team',
      security: 'Security',
    },
    security: {
      title: 'Security',
      subtitle: "Control which networks can reach your organization's data.",
      allowlistTitle: 'IP allowlist',
      allowlistHint:
        'When set, only these IP addresses and ranges can access this organization. Leave empty to allow any network. Accepts IPv4, IPv6, and CIDR ranges (e.g. 203.0.113.4, 203.0.113.0/24, 2001:db8::/32).',
      currentIp: 'Your current IP address is',
      emptyState: 'No IP restrictions — this organization is reachable from any network.',
      ruleValueLabel: 'IP address or range',
      ruleValuePlaceholder: '203.0.113.0/24',
      ruleLabelLabel: 'Label',
      ruleLabelPlaceholder: 'Label (optional)',
      invalidRule: 'Enter a valid IP address or CIDR range.',
      removeRule: 'Remove this rule',
      addRule: 'Add IP address',
      save: 'Save changes',
      saved: 'IP allowlist saved.',
      saveError: 'Could not save the IP allowlist. Please try again.',
    },
    saveStatus: {
      saving: 'Saving...',
      saved: 'Saved',
      error: 'Could not save. Try again.',
    },
    org: {
      title: 'Organization',
      subtitle: "Manage your organization's name and details.",
      nameLabel: 'Organization name',
      adminNotice: 'You must be an admin to change the organization name.',
    },
    profile: {
      title: 'Profile',
      subtitle: 'Manage your personal details and sign-in.',
      nameLabel: 'Your name',
      accountTitle: 'Account',
      emailCurrent: 'Your email is {{email}}.',
    },
    changeEmail: {
      openButton: 'Change email',
      title: 'Change email',
      requestBody: "Enter your new email address. We'll send a verification code to it.",
      newEmailLabel: 'New email',
      invalidEmail: 'Enter a valid email address.',
      requestError: "We couldn't send a code to that address. Try again.",
      verifyBody: 'Enter the 6-digit code we sent to {{email}}.',
      badCode: 'That code is incorrect or has expired.',
      confirm: 'Confirm',
      resend: 'Resend code',
      resendIn: 'Resend in {{seconds}}s',
      success: 'Your email has been changed to {{email}}.',
    },
    changePassword: {
      openButton: 'Change password',
      title: 'Change password',
      body: 'Enter your current password and a new one.',
      currentLabel: 'Current password',
      newLabel: 'New password',
      newHint: 'At least {{min}} characters.',
      tooShort: 'Your new password must be at least {{min}} characters.',
      wrongCurrent: 'Your current password is incorrect.',
      submit: 'Update password',
      success: 'Your password has been updated.',
    },
    team: {
      title: 'Team',
      subtitle: 'Manage who has access to your organization.',
      you: 'You',
      columns: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
        status: 'Status',
        actions: 'Actions',
      },
      statusActive: 'Active',
      statusPending: 'Pending',
      resend: 'Resend',
      resendSuccess: 'Invite resent to {{email}}.',
      resendError: "We couldn't resend that invite. Try again.",
      roleFor: 'Role for {{name}}',
      removeMember: 'Remove {{name}}',
      removeTitle: 'Remove member',
      removeBody: 'Remove {{name}} from this organization? They will lose access.',
      removeConfirm: 'Remove',
      removeError: "We couldn't remove that member. Try again.",
      inviteTitle: 'Invite a teammate',
      inviteSubtitle: "Send an invite by email. They'll join once they accept.",
      inviteNameLabel: 'Name',
      inviteEmailLabel: 'Email',
      inviteRoleLabel: 'Role',
      inviteSend: 'Send invite',
      inviteError: "We couldn't send that invite. Try again.",
      adminNotice: 'You must be an admin to invite team members.',
    },
  },
  uploader: {
    // ── Page + wizard shell ──
    // Docked-header page title for the new-upload flow (the in-content PageHeader was
    // removed; the title + Back now live in the app shell's docked TopBar).
    newUpload: 'New Upload',
    // Docked-header title when reopening a DRAFT back into the wizard to finish it.
    resumeUpload: 'Resume Upload',
    // Toast when a draft can't be reopened (e.g. abandoned before it was analyzed).
    resumeError: "Couldn't reopen this draft. Please start a new upload.",
    // Compact stepper stage labels (descriptions were dropped to keep it small).
    stageChooseFiles: 'Choose Files',
    stagePreflight: 'PII Preflight',
    stageMapping: 'Column Mapping',
    stageReview: 'Review',
    continue: 'Continue',
    // Loading state while the entry plan resolves on a fresh mount.
    preparingUpload: 'Preparing your upload…',

    // ── Step: upload guide (the interactive, educational walkthrough) ──
    guide: {
      // Navigation buttons (shared across screens).
      back: 'Back',
      next: 'Next',
      gotIt: 'Got it',

      // 1. Welcome
      welcome: {
        title: 'Upload Guide',
        body: "We'll walk you through the process and ask a few questions to help tailor the experience to your needs.",
        subheading: 'Flexible Uploads',
        subtext: "You don't need to match a rigid template. Upload the data in whatever format you have it in. As long as it includes the data we need, it will work.",
        illustrationAlt: 'Animated chart illustrating loan-portfolio analytics',
      },

      // 2. PII
      pii: {
        title: 'PII never leaves your computer',
        body: "We'll strip out PII (personally identifiable information) before the file gets uploaded.",
        // Point 1 — text beside the drag → strip → upload animation.
        dragTitle: 'Drag and drop your file',
        dragBody: "Before the file gets uploaded, we'll strip out PII.",
        // Point 2 — the small inline scramble indicator.
        scrambleTitle: 'Loan ID & Tax ID are scrambled',
        scrambleBody:
          'These values are irreversibly scrambled so that we can recognize these loans in future uploads without storing any PII.',
        // Caption centered under the two scramble arrows.
        irreversiblyScrambled: 'Irreversibly Scrambled',
      },

      // 3. Snapshots
      snapshots: {
        title: 'You upload snapshots over time',
        body: 'A snapshot is a picture of your loan portfolio at a single point in time. You can bulk upload historical snapshots, and then upload new snapshots each month.',
        rowsAreLoans: 'Each row is one loan.',
        colsAreCharacteristics: 'Each column is a characteristic of that loan at that moment.',
        cadenceTitle: 'Any cadence works',
        cadenceBody: 'Upload monthly, quarterly, or yearly, whatever your core system produces.',
        cadenceMonthly: 'Monthly',
        cadenceQuarterly: 'Quarterly',
        cadenceYearly: 'Yearly',
      },

      // 4. Origination
      origination: {
        title: 'Where is your origination data?',
        body: 'Origination data is the facts about a loan when it was made: its origination date and original loan amount. Where does yours live?',
        inSnapshots: "It's in the snapshots",
        inSnapshotsHelp: 'The origination fields are columns in your snapshot rows.',
        separateFile: "It's in a separate file",
        separateFileHelp: 'Origination data arrives as its own file.',
        estimated: "It's not available",
        estimatedHelp: 'The product will estimate it.',
        estimateExplanation:
          'When origination data is missing, the product estimates the origination date from the first month a loan appears, and the original balance from the first balance it sees for that loan.',
      },

      // 5. Charge-off
      chargeOff: {
        title: 'Where are your charge-offs?',
        body: 'To model credit loss, the product needs charge-off events. Where do yours live?',
        inSnapshots: "They're in the snapshots",
        inSnapshotsHelp: 'The charge-off signal is in your snapshot rows.',
        separateFile: "They're in a separate transactions file",
        separateFileHelp: 'Charge-offs arrive as their own transactions file.',
      },

      // 6. Prepayment
      prepayment: {
        title: 'How can we detect prepayment?',
        body: 'To model prepayment, the product must be able to detect partial and full prepayment. Which of these does your data provide?',
        amountInSnapshots: 'The snapshots show the prepayment amount',
        amountInSnapshotsHelp: 'The prepayment amount is given directly in each snapshot.',
        expectedVsActual: 'The snapshots show expected vs. actual payment',
        expectedVsActualHelp: 'The product compares the expected and actual payment to detect prepayment.',
        // RB-8: the speed-denominator requirement, stated plainly BEFORE finalize —
        // matches PRODUCT.md §Upload Guide step 6.
        schedNote:
          'Either way, measuring prepayment speed also needs Scheduled Principal (due or paid) in your data. Loans without it won\'t receive prepayment speeds — adding that one field is what unlocks them.',
      },

      // 7. Summary
      summary: {
        title: "Here's your upload plan",
      },
    },

    // ── Upload plan summary (shared: guide summary screen + collapsed upload section) ──
    // Plain-language prose derived from the UploadPlan enums + catalog labels.
    planSummary: {
      title: 'Your upload plan',
      // The plain-English one-liner. `primary` is the snapshot name; `separate` is
      // the and-joined list of the optional separate files the plan calls for.
      sentenceOne: "You'll upload {{primary}}.",
      sentenceMany: "You'll upload {{primary}}, plus separate {{separate}}.",
      // Collapsed-section affordances on the upload screen.
      show: 'Show details',
      hide: 'Hide details',
    },

    // ── Plan files (structured upload-plan summary: per-file sections + templates) ──
    // Drives both the on-screen UploadPlanSummary body and the printable plan PDF.
    planFiles: {
      title: "Here's your upload plan",
      subtitle: 'Based on your answers, this is what we\'ll need.',
      downloadPdf: 'Download Plan as PDF',
      pdfError: "Couldn't generate the PDF. Please try again.",
      snapshot: {
        heading: 'Loan Snapshots',
        summary:
          'Snapshots of your loan portfolio over time, one row per loan, captured at each point in time.',
        withOrigination: 'Each snapshot also includes the loan\'s origination data.',
      },
      origination: {
        heading: 'Origination Files',
        summary:
          'Your origination data (origination date and original amount) as its own file, linked to the snapshots by Loan ID.',
      },
      transaction: {
        heading: 'Transaction Files',
        summary:
          'Your loan transactions — charge-offs, recoveries, and any payoff or prepayment events — as their own file, linked to the snapshots by Loan ID.',
      },
      fieldsHeading: 'Recommended fields',
      note1: "You don't need to follow a rigid template or match column names exactly.",
      note2: "Upload the data as you have it. As long as everything is in the file, we'll make it work.",
      note3prefix: 'If you\'d rather, you can download the template in',
      downloadTemplateHeading: 'Download template',
      templateHint: "You don't need to follow a strict template, but templates are available.",
      templateCsv: 'CSV',
      templateXlsx: 'XLSX',
      pdfTemplateNote: 'Templates are available to download from the upload page if you\'d like them.',
    },

    // File-type identities — the ONE name per type, used everywhere it is named
    // (Choose Files cards, staged-file groups, Preflight + Mapping tabs, the modal
    // sentence, the history count columns, modeling provenance). Plural, since a
    // customer can upload more than one of each.
    fileTypes: {
      snapshot: {
        title: 'Loan Snapshots',
        description: 'Snapshots of your loan portfolio at a point in time, one row per loan.',
      },
      origination: {
        title: 'Origination Files',
        description: 'The original terms and balances of each loan when it was made.',
      },
      transaction: {
        title: 'Transaction Files',
        description: 'Individual loan transactions — charge-offs, recoveries, payoffs, prepayments.',
      },
    },

    // ── Step: upload (file select) ──
    upload: {
      chooseTitle: 'Choose your files',
      chooseSubtitle:
        'Add your files by type. PII is removed in your browser before anything is uploaded.',
      // The three file-group cards. Any non-empty combination can continue.
      groups: {
        addHint: 'Click to add files',
        filesStaged_one: '{{count, number}} file added',
        filesStaged_other: '{{count, number}} files added',
        fileCount_one: '{{count, number}} file',
        fileCount_other: '{{count, number}} files',
        sameSchemaHint: 'All files in this group must have the same columns, in the same order.',
      },
      // Shown when an Excel (.xlsx) file is added — nudge large portfolios to CSV.
      // The 50 MB cap is mentioned ONLY in these on-drop notices, never in
      // always-on copy.
      xlsxNotice:
        'Excel files work. For a large portfolio we recommend exporting to CSV and uploading that instead, which is faster and lighter on your browser. Excel uploads are limited to 50 MB.',
      // Shown instead of xlsxNotice when an added Excel file's row count is
      // estimated well past a size where the whole-workbook-in-memory model gets
      // uncomfortable. More specific than xlsxNotice, so it takes precedence.
      // Bulk and backfill uploads (many months of history at once) should use CSV.
      xlsxLargeRowCount:
        "This Excel file looks like it has around {{count, number}} rows. Excel loads the whole file into your browser's memory at once, so for a file this size we strongly recommend exporting to CSV instead, especially for a bulk or backfill upload. CSV streams in and has no row limit.",
      xlsxTooLarge:
        'This Excel file is too large to upload here. Export it as CSV and upload that instead; CSV has no size limit. Excel uploads are limited to 50 MB.',
      // Whole-upload Excel budget (file count + aggregate size). Excel can't be
      // streamed, so a batch is capped to keep the browser responsive; CSV is free.
      xlsxTooManyFiles:
        'You can add up to {{max, number}} Excel files per upload. Export some to CSV, or split them across separate uploads.',
      xlsxBatchTooLarge:
        'Those Excel files are too large together. Excel is capped at {{max, number}} MB per upload; export some to CSV (it has no size limit) or split them across separate uploads.',
      removeFile: 'Remove file',
      columnsRead: '{{count, number}} columns read',
      sameFormatError:
        'All {{fileType}} files must share the same columns, in the same order. Remove the mismatched file or upload it separately.',
      noFiles: 'Add at least one file to continue.',
      restartGuide: 'Restart Guide',
      // The upload modal opened from a file-group card.
      modal: {
        subtitle:
          'Upload one or more {{fileType}}. All {{fileType}} in a batch must have the same format.',
        dropPrompt: 'Drag and drop your files here',
        browse: 'Browse files',
      },
    },

    // ── Step: PII preflight ──
    preflight: {
      title: 'Protect sensitive columns',
      reassurance:
        'This review happens entirely in your browser. Sensitive values never leave your device. Only the columns you allow are uploaded, and IDs are protected before they go.',
      forFileType: 'For your {{fileType}}',
      joinKeyHint:
        'Loan ID links uploaded rows into the portfolio in v1. Tax ID is protected and retained for future matching, but it is not used as a fallback yet.',
      loanIdLabel: 'Loan ID column',
      loanIdHelp: 'The primary join key. Protected before upload, never stored as-is.',
      loanIdPlaceholder: 'Select the Loan ID column',
      loanIdNone: 'None',
      taxIdLabel: 'Tax ID column',
      taxIdHelp: 'Protected before upload if selected. Stored for future matching, not used as a v1 fallback.',
      taxIdPlaceholder: 'Select the Tax ID column',
      taxIdNone: 'None',
      taxIdMissingWarning: 'No Tax ID column selected. That is fine when this group has a Loan ID.',
      joinKeyRequired: 'Select a Loan ID column to continue. Tax ID matching is not used in v1.',
      dropTitle: 'Is there any more PII?',
      dropHelp:
        'Mark fields as PII and they will be excluded from upload. The marked fields below have been detected as potentially including PII.',
      flaggedPii: 'Likely PII',
      confirmContinue: 'Confirm and continue',
      columnSearchPlaceholder: 'Search columns',
      columnNoMatches: 'No matching columns',
      // A column with a blank header name, labelled by position so it is still
      // selectable / droppable.
      unnamedColumn: '(unnamed)',
      // Disambiguates a duplicated (or blank) header by its 1-based column
      // position, e.g. "Email (column 4)".
      columnPositionSuffix: '{{name}} (column {{position}})',
      // Surfaced when a file has duplicate or blank header names, so the user
      // knows why some options are disambiguated by position.
      duplicateHeadersNotice:
        'Some columns share a name or have no name. They are listed by position (e.g. "column 4") so you can pick the right one.',
    },

    // ── Step: processing ──
    processing: {
      title: 'Preparing your files',
      description: 'Scrubbing and uploading. Sensitive columns are removed and IDs protected in your browser before anything is sent.',
      phaseParsing: 'Reading',
      phaseScrubbing: 'Scrubbing',
      phaseUploading: 'Uploading',
      phaseIngesting: 'Ingesting data',
      phaseAnalyzing: 'Analyzing',
      phaseComplete: 'Complete',
      rowsProcessed: '{{count, number}} rows processed',
      overallLabel: 'Overall progress',
      overallCount: '{{completed, number}} of {{total, number}} files complete',
      failed: 'Something went wrong preparing this file.',
      retry: 'Retry',
      // B1 — shown once every group's scrubbed bytes have left the browser: the server
      // finishes ingest + analysis on its own, so the tab can be closed and the upload
      // resumed later. Info-toned, not success: the cards below are still working, so
      // the notice claims only "safe to leave" (the page title stays "Preparing your
      // files"; only the description swaps in).
      safeTitle: 'Safe to leave this page',
      safeDescription:
        'Every file has finished uploading. We are ingesting and analyzing them now, then column mapping opens for each group.',
      safeBody:
        'You can safely leave this page. We will keep working in the background, and you can resume from Upload History any time to finish the review.',
    },

    // ── Step: mapping (inverted, source-column-centric) ──
    mapping: {
      title: 'Review column mapping',
      description: 'Map each of your columns to a standard field or to a custom attribute. Nothing is dropped; custom attributes are kept.',
      columnsHeading: 'Your columns',
      // Section headings shown when a file partially matches a previous upload: the
      // columns split into what needs attention vs. what we already recognized.
      sectionNewOrChanged: 'New or Changed Columns',
      sectionRecognized: 'Recognized Columns',
      mapColumnTo: 'Map column',
      customAttribute: 'Custom attribute',
      inferredType: 'Type',
      // Inferred-type display labels (used for the type chip + the wrong-type
      // error copy). Keyed by the `InferredType` union members.
      typeNumeric: 'numeric',
      typeDate: 'date',
      typeText: 'text',
      typeCategorical: 'categorical',
      // Per-row provenance badge: only the deterministic name-match is surfaced.
      // AI is not badged (owner feedback), and previous-upload is not badged per row
      // (the upload-level notice + the partial-match "Recognized Columns" grouping
      // communicate it without tagging "tons" of rows — owner feedback).
      provenanceFallback: 'Matched by name',
      // Short provenance hints shown next to a suggested option in the combobox.
      provenanceHintFallback: 'name match',
      provenanceHintPrevious: 'previous',
      nonFitting: '{{count, number}} values treated as empty',
      nonFittingTitle: 'Values treated as empty',
      duplicateTarget: 'Another column also maps to this standard field. Review to resolve.',
      finishMapping: 'Continue to modeling review',
      // Combobox (searchable, sectioned target picker).
      searchPlaceholder: 'Search fields…',
      sectionSuggested: 'Suggested',
      sectionCustom: 'Custom',
      sectionAll: 'All fields',
      noFieldMatches: 'No matching fields',
      // Scrambled type label — protected Loan/Tax ID columns are one-way HMACs.
      scrambledType: 'Scrambled',
      // Wrong-type BLOCKING error (an included column mapped to a field whose
      // acceptable types exclude this column's inferred type).
      wrongType:
        '"{{field}}" needs a {{expected}} column, but this column is {{actual}}.',
      markAsCustom: 'Mark as Custom Attribute',
      // ── Editable field types on the mapping screen (built later; keys now) ──
      // Marker shown on a column mapped to a field that already exists in the portfolio.
      existingFieldMarker: 'Existing field',
      // Used ONLY by the non-blocking durable-type conflict note (the existing-field
      // row shows its type inline next to the marker instead).
      existingFieldNote: 'This field already exists, stored as {{type}}.',
      // Opens the shared retype modal to change the existing field's type.
      changeType: 'Change type',
      // Label above the percent-variant control for a percentConfigurable standard
      // target (interest_rate). The choice is org-wide, not just for this file.
      percentVariantLabel: 'Rate format (applies to all uploads)',
      // The blocking notice when the column's type does not fit the existing field's
      // registered type and the values would be emptied. F125: {{willNull}} is how many of the
      // file's {{total}} non-blank values won't fit — the incoming FILE, not already-stored data,
      // and a real denominator (was "N of N stored values", always 100%).
      retypeConsequence:
        '{{willNull, number}} of {{total, number}} values in this file won’t fit this type and will become empty.',
      excludeColumn: 'Exclude column',
      // Include/exclude toggle + the auto-exclude (PII) note.
      includeLabel: 'Include this column',
      excludedNote: 'Excluded, won\'t be used.',
      autoExcludedPii: 'Auto-excluded: values look like PII. Re-include if it\'s safe.',
      // The dropped-columns notice (F17 — FOUNDER-RULED wording, 2026-07-17):
      // "during upload" deliberately ambiguous; "the system detected", never
      // "we saw"; calm, no exposure-panic framing. {{columns}} is the quoted,
      // comma+and-joined column list.
      piiDroppedColumns_one:
        'Column {{columns}} was dropped during upload — the system detected it may contain personal information. You can re-include it below.',
      piiDroppedColumns_other:
        'Columns {{columns}} were dropped during upload — the system detected they may contain personal information. You can re-include them below.',
      // The "and" joining the final column name in the dropped-columns list.
      piiDroppedAnd: 'and',
      // File-level data-quality signals (P5 — accept-and-warn, never blocking).
      dataQualityRaggedRows_one:
        '{{count, number}} row had a different number of cells than the header row. It was kept; cells past the last column were not read.',
      dataQualityRaggedRows_other:
        '{{count, number}} rows had a different number of cells than the header row. They were kept; cells past the last column were not read.',
      dataQualityParseErrors_one:
        '{{count, number}} formatting issue was found while reading the file. The affected rows were still read.',
      dataQualityParseErrors_other:
        '{{count, number}} formatting issues were found while reading the file. The affected rows were still read.',
      dataQualityOverflow_one:
        '{{count, number}} value was too large to store as a number and is treated as empty. It remains unchanged in your uploaded data.',
      dataQualityOverflow_other:
        '{{count, number}} values were too large to store as numbers and are treated as empty. They remain unchanged in your uploaded data.',
      // Continue-gate summary near the mapping Continue button.
      typeErrorSummary_one: 'Resolve {{count, number}} type error to continue.',
      typeErrorSummary_other: 'Resolve {{count, number}} type errors to continue.',
      groupNeedsReview: 'Needs review',
      groupAppliesToAll_one: 'This mapping applies to your {{type}}.',
      groupAppliesToAll_other: 'This mapping applies to all {{count, number}} {{type}}.',
      // Value classification panel (a column mapped to a status OR a flag field).
      valueClassify: {
        title: {
          status: 'What does each status mean?',
          flag: 'What does each value mean?',
        },
        hint: {
          status:
            'Every system uses different status words, so we never guess. Tell us which values mean a loan paid off or charged off — the rest are normal servicing statuses.',
          flag: 'Every system flags events differently, so we never guess. Tell us which values mark the event and which do not.',
        },
        progress: '{{classified, number}} of {{total, number}} classified',
        placeholder: 'Classify…',
        rowCount_one: '({{count, number}} row)',
        rowCount_other: '({{count, number}} rows)',
        // The status vocabulary is shared across the status fields.
        options: {
          active: 'Neither / active',
          paid_off: 'Paid off',
          charged_off: 'Charged off',
        },
        // Flag options read per-field (the "event" a charge-off flag marks differs
        // from a payoff flag's), so they are scoped by the flag field key.
        flagOptions: {
          charge_off_flag: {
            event: 'Marks a charge-off',
            not_event: 'Does not',
          },
          payoff_flag: {
            event: 'Marks a payoff',
            not_event: 'Does not',
          },
          prepayment_flag: {
            event: 'Marks a full prepayment',
            not_event: 'Does not',
          },
        },
        // Suggestions: a value the AI (or the keyword heuristic) pre-selected.
        suggestedTag: 'Suggested',
        suggestedReview_one: '{{count, number}} suggested — review before continuing.',
        suggestedReview_other: '{{count, number}} suggested — review before continuing.',
        tooManyTitle: {
          status: 'Too many distinct values for a status field',
          flag: 'Too many distinct values for a flag field',
        },
        tooManyBody:
          'This column has more than {{max, number}} distinct values — a status or flag column usually has a handful. Check that this column is really the one you mapped (it may be an ID or free-text column).',
        loadErrorTitle: 'Couldn\'t load this column\'s values',
        loadErrorBody: 'Try again in a moment — the values are needed to classify this column.',
      },
      // Reporting-basis chip (a column mapped to a flow/amount field — RB-6).
      // A running total misread as monthly activity would count the same loss
      // once per snapshot, so the answer is confirmed, never guessed.
      reportingBasis: {
        title: 'How is this amount reported?',
        hint: 'Systems export amounts as the month\'s activity, a running total, or a year-to-date total. We checked your data and pre-filled the best match — please confirm.',
        ambiguousHint: 'We can\'t tell from your data: these values only ever grow, which could be monthly amounts or a running total. Choose one to continue — reading it wrong would count the same loss more than once.',
        // Residual #3: the flat-repeated shape (the same nonzero value re-exported every
        // month — a persisted life-to-date figure) gets a more specific nudge.
        flatRepeatedHint: 'These values repeat unchanged month after month — that\'s usually a running total (the same life-to-date amount re-exported each snapshot). Choose one to continue — reading it wrong would count the same loss more than once.',
        placeholder: 'Choose…',
        options: {
          per_period: 'Amount for that month',
          running_total: 'Running total (life-to-date)',
          ytd: 'Year-to-date (resets in January)',
        },
        suggestedTag: 'Suggested',
        loadErrorTitle: 'Couldn\'t check this column\'s reporting basis',
        loadErrorBody: 'Try again in a moment — the check is needed before this column can be used.',
      },
      // Date-format chip (a column mapped to a date field — WS-E instance #2).
      // Reading 04/07/2025 with the wrong day/month order shifts every
      // observation month, so the order is declared per column: read from the
      // data where the data proves it, otherwise ASSUMED US Month/Day
      // (2026-07-18 ruling, US-only customers) — shown and correctable, never
      // blocking.
      dateFormat: {
        title: 'How are these dates written?',
        // The prefill was PROVEN by the column's own values (a day > 12 pins the order).
        matchesDataHint: 'We read the order from your data — values like day 13+ pin it. Correct it if that\'s wrong.',
        // The ambiguous case: every day <= 12, so the data cannot say. US default.
        usDefaultHint: 'Your dates could be read either way (every day is 12 or less), so we assume the US Month/Day order. If this column is day-first, change it here before the data is used.',
        // A surviving stored suggestion from an earlier upload; this file's data is silent.
        storedHint: 'Carried over from your earlier uploads of this column. Correct it if this file is different.',
        // The customer's own sticky declaration.
        declaredHint: 'You chose this order — it applies to every upload of this column until you change it.',
        // Conflict: some cells prove Month/Day and others Day/Month — whichever
        // order is chosen, the non-fitting cells read as no value (never guessed).
        conflictHint: 'Some values in this column pin Month/Day and others Day/Month — it can\'t be both. Whichever you choose, dates that don\'t fit are treated as missing, never guessed.',
        suggestedTag: 'Suggested',
        loadErrorTitle: 'Couldn\'t check this column\'s date format',
        loadErrorBody: 'Try again in a moment — the dates are read using this format.',
      },
    },

    // ── Previous-match notice ──
    prevMatch: {
      exactTitle: 'Matches a previous upload',
      exactBody: 'This file has the same format as "{{name}}". We\'ve pre-filled the mapping from last time.',
      partialTitle: 'Slightly different from a previous upload',
      partialBody: 'This file looks similar to "{{name}}", with a few changes. We\'ve pre-filled what we recognized. Review the changes below.',
      newTitle: 'New file format',
      newBody: 'We don\'t recognize this format from previous uploads. Review the mapping below.',
      // No first-time copy: the very first upload shows no notice (owner feedback).
      showChanges: 'Show changes',
      hideChanges: 'Hide changes',
    },

    // ── Diff summary ──
    diff: {
      title: 'What changed',
      added: 'New columns',
      dropped: 'Removed columns',
      renamed: 'Renamed columns',
      typeChanged: 'Changed type',
      renamedItem: '{{from}} → {{to}}',
      typeChangedItem: '{{name}}: {{from}} → {{to}}',
      noChanges: 'No structural changes detected.',
    },

    // ── Step: Review (the unified end-of-flow: reconciliation + modeling + finalize) ──
    review: {
      title: 'Review your upload',
      subtitle:
        'A final review of what this upload adds to your portfolio, and what it can model.',
      // Shown on the Review step while the row-level review is still computing
      // (we navigate here immediately, then fill it in when the server responds).
      loadingTitle: 'Reviewing your upload',
      loadingSubtitle:
        'We\'re joining your uploaded rows with your existing portfolio and working out what each result can be modeled for. This can take a moment for a large upload.',

      // Verdict gate
      readyBanner: 'Ready to finalize',
      readyBody:
        'This upload contributes valid loan-tied data. Loans that are not model-ready yet keep improving with later uploads.',
      blockedBanner: 'Cannot finalize yet',
      blockedBody:
        'This upload has no usable data tied to a Loan ID, so there is nothing to add to your portfolio.',
      // Attention verdict: finalizable, but a material share of loans can't be modeled.
      attentionBanner: "Some loans can't be modeled yet",
      attentionBody:
        'You can finalize now; this upload still adds valid data. But a meaningful share of loans is missing what some results need, so review the gaps below (or add the missing data in a later upload).',
      // Verdict headline detail (§4.3): the strongest gap, in the data's own
      // terms. {{output}} is the modeled result name, {{percent}} the share of
      // loans that can't be produced.
      gapCoverageZero: 'None of your loans have what {{output}} needs.',
      gapCoverage: "{{percent}}% of your loans can't be modeled for {{output}} yet.",

      // Reconciliation ledger
      reconciliation: 'Reconciliation',
      rowsInUpload: 'Rows in upload',
      linkedRows: 'Linked to a loan',
      unlinkedNote: '{{value}} without a Loan ID',
      contributingRows: 'Contributing portfolio data',
      loansInUpload: 'Loans in this upload',
      newLoans: '{{value}} new',
      matchedLoans: '{{value}} matched',
      allNew: 'All new to your portfolio',

      // What you can model
      whatYouCanModel: 'What you can model',
      whatYouCanModelHelp:
        'What each result can be modeled for, once this upload is joined with your portfolio.',
      outputs: {
        credit_loss: 'Credit loss',
        full_payoff: 'Full payoff',
        partial_prepayment: 'Partial prepayment',
      },
      readyLoans: '{{ready, number}} of {{total, number}} loans ready',
      modeledBy: 'Modeled by',
      noMethods: 'No loans are ready for this result yet.',
      // RB-8: the partial-prepayment card states the speed-denominator requirement
      // plainly — matches PRODUCT.md §Review (readiness surface).
      prepaySchedNote:
        'Loans missing Scheduled Principal (due or paid) won\'t receive prepayment speeds — adding that one field is what unlocks them.',
      missingCritical_one: '{{count, number}} loan still needs critical data.',
      missingCritical_other: '{{count, number}} loans still need critical data.',
      coverageDetail: 'Coverage detail',
      coverageHelp: 'How each ready loan is modeled, and what would bring the rest in.',
      methodShare_one: '{{percent}}% · {{count, number}} loan',
      methodShare_other: '{{percent}}% · {{count, number}} loans',
      provenanceFrom: 'from {{column}}',
      // Upgrade nudge — move loans onto the preferred method (benefit worded per method).
      upgrade_one: 'Add {{fields}} and 1 loan can use {{method}}: {{benefit}}.',
      upgrade_other: 'Add {{fields}} and {{count, number}} loans can use {{method}}: {{benefit}}.',
      // Unready remainder — what to supply to bring more loans in.
      remainder_one: "1 loan isn't ready for this result yet.",
      remainder_other: "{{count, number}} loans aren't ready for this result yet.",
      remainderNeed: 'Add to bring more in:',
      remainderField_one: '{{field}} · {{count, number}} loan',
      remainderField_other: '{{field}} · {{count, number}} loans',
      remainderMore_one: '+1 more field',
      remainderMore_other: '+{{count, number}} more fields',

      // Gap guide (shown ONLY on outputs the verdict flagged as gaps): the
      // universal base once at the top, then options A/B/C with per-field
      // coverage. Event-shaped fields read as INCIDENCE, never as "missing":
      // a loan without a recorded charge-off is not missing data.
      guide: {
        title: 'How to close this gap',
        intro:
          'Any one option gives {{output}} what it needs. Fields are matched loan by loan, so partial coverage still counts.',
        everyOptionNeeds: 'Every option needs',
        optionTag: 'Option {{letter}}',
        or: 'or',
        plus: 'plus',
        coverage: 'on {{percent}}% of loans',
        notInData: 'not in your data yet',
        incidence: '{{percent}}% of loans have one recorded',
        incidenceElsewhere: 'recorded elsewhere in your portfolio',
        incidenceNone: 'none recorded in your data yet',
        eventNote:
          'Event fields, like charge-offs and payoffs, are only recorded on loans where the event happened. A loan without one is not missing data: that recorded history is exactly what the curves are built from.',
      },

      // Origination (informational — never blocks)
      origination: 'Origination',
      originationHelp:
        'Origination anchors each loan for aging. When a field is not provided, we estimate it: the origination date from the first month a loan appears, and the original balance from the first balance we see. Those loans are still modeled, aged from their first appearance, and flagged Estimated.',
      originationProvided: 'Provided',
      originationEstimated: 'Estimated',
      // Term provenance (rides the origination ledger).
      termLabel: 'Loan term',
      termProvided: 'Provided',
      termDerived: 'From maturity date',
      termMissing: 'Missing',

      // Next actions
      actionsReady: 'Worth a look before you finalize',
      actionsBlocked: 'What to fix',
      allClear: 'No issues found. This upload is ready to finalize.',

      back: 'Back to mapping',
      continue: 'Finalize upload',
      finalizing: 'Finalizing…',

      // Progressive manifest (B1): the full event composition + per-file readiness,
      // shown while files are still preparing. Finalize is blocked until all are ready.
      manifestPreparing: 'Preparing your upload — {{ready, number}} of {{total, number}} files ready',
      manifestReadyLabel: 'Ready',
      manifestPreparingLabel: 'Preparing',
      manifestUnnamed: 'Uploaded file',
    },

    // ── Step: done ──
    done: {
      title: 'Upload complete',
      body: '{{count, number}} file accepted and ready for modeling.',
      body_other: '{{count, number}} files accepted and ready for modeling.',
      viewPortfolio: 'View Portfolio',
      startAnother: 'Start another upload',
    },

    // ── Minimum-field-set option labels (modeling-readiness.ts labelKeys) ──
    creditLoss: {
      directNetLoss: 'Direct net loss',
      directNetLossBenefit:
        'it measures the realized loss directly, instead of estimating it from gross charge-offs',
      grossChargeOff: 'Gross charge-off',
      explicitEvent: 'Charge-off event',
    },
    // ── WS-H parity-exclusion registry nudges (ReviewParityExclusion.nudgeKey) ──
    // Each counted exclusion the parity measure produces carries one of these
    // unlock nudges; the data-basis surfaces (WS-G) render them.
    parityExclusions: {
      // F54/F171: no usable term (missing columns, term 0, maturity ≤ origination).
      noTerm:
        'Supply a Term (Months) column — or a Maturity Date after each loan’s origination — to bring these loans into the curves.',
      // F46/F53: a date field is present but never parses for these loans.
      unparseableDate:
        'These values don’t read as dates. Correct the column’s date format (or re-export the column) to unlock these loans.',
      // F170: mapped status/flag columns whose values are not yet classified.
      noEventVocabulary:
        'Classify a status or flag value as a payoff event to unlock payoff modeling for these loans.',
      // F55/F170: a charge-off is named (flag/status) but no loss amount exists.
      noLossAmount:
        'These loans carry a charge-off marker but no loss amount. Supply a Net Charge-Off Amount (or Gross Charge-Off and Recovery) column to measure their losses.',
      // R2: first seen in the earliest observed month with no origination date.
      unanchoredFirstMonth:
        'These loans first appear in your earliest reported month, so their age is unknown. A two-column file with Loan ID and Origination Date unlocks them.',
    },
    fullPayoff: {
      directEvent: 'Direct payoff event',
      directEventBenefit:
        'it reads payoffs from an explicit signal, instead of inferring them from loan status',
      closureClass: 'Closure classification',
      maturityAware: 'Maturity-aware closure',
    },
    partialPrepay: {
      directPartial: 'Direct partial prepayment',
      directPartialBenefit:
        'it uses the reported prepayment amount directly, instead of deriving it from payment schedules',
      actualVsSched: 'Actual vs. scheduled principal',
    },

    // ── Upload history (list) ──
    history: {
      title: 'Uploads',
      subtitle: 'Past uploads and their modeling readiness.',
      newUpload: 'New upload',
      // Resume banner: shown when the most recent upload is an unfinished (draft) event.
      resumeTitle: 'Pick up where you left off',
      resumeBody:
        'You have an upload in progress from {{date}}. Your files are saved, so you can continue right where you stopped.',
      resumeAction: 'Resume upload',
      loadMore: 'Load more',
      loadError: 'Could not load your uploads. Please try again.',
      emptyTitle: 'No uploads yet',
      emptyBody:
        'Upload your first loan file to map its columns and check modeling readiness.',
      openRow: 'Open upload',
      deleteUpload: 'Delete upload',
      deleteTitle: 'Delete upload?',
      deleteBody:
        'This removes the upload from the active portfolio model and recomputes from the remaining uploaded data. Audit history is preserved.',
      deleteConfirm: 'Delete upload',
      deleteError: 'Could not delete the upload. Please try again.',
      // Status pill labels — an unaccepted upload is a resumable draft.
      statusDraft: 'Draft',
      statusAccepted: 'Accepted',
      columns: {
        // The event date + time column. The per-type count columns are headed by
        // each type's standardized name (uploader.fileTypes.*.title).
        uploads: 'Uploads',
        status: 'Status',
        uploadedBy: 'Uploaded by',
      },
    },

    // ── Reopen / edit a past upload (detail) ──
    detail: {
      // Docked-header title for a reopened upload ("Upload from {date}"); the fallback
      // shows while the detail (and its createdAt) is still loading.
      titleFrom: 'Upload from {{date}}',
      titleFallback: 'Upload',
      back: 'Back to uploads',
      isolationNote:
        'Editing this mapping affects only this file; other uploads are unchanged.',
      save: 'Save mapping',
      saving: 'Saving…',
      saveError: 'Could not save the mapping. Please try again.',
      saved: 'Mapping saved.',
      loadError: 'Could not load this upload.',
      reset: 'Discard changes',
      deleteFile: 'Delete file',
      deleteFileTitle: 'Delete file?',
      deleteFileBody:
        'This removes the file from the active portfolio model and recomputes the upload from the remaining files. Audit history is preserved.',
      deleteFileConfirm: 'Delete file',
      deleteFileError: 'Could not delete the file. Please try again.',
    },
  },
  // ── Field value types + display subtypes (the editable-field-types feature) ──
  // Type labels for the durable value type of a field. Shared by the type pickers,
  // the Custom Attributes table, the retype modal, and the mapping screen. Keyed by
  // the `InferredType` union members so `t(\`fieldTypes.${type}\`)` resolves.
  fieldTypes: {
    numeric: 'Numeric',
    date: 'Date',
    categorical: 'Categorical',
    text: 'Text',
    // aria-label for the type Select control.
    typeLabel: 'Type',
  },
  // Display subtype labels for a numeric field. Keyed by the `NumericSubtype` union
  // members so `t(\`numericSubtypes.${subtype}\`)` resolves. The two percent variants
  // name the INPUT convention the customer is declaring for the column: `percentage`
  // is a fraction column (0.01 in the file means 1%), scaled ×100 into percent-points
  // at projection; `percent_points` is already in percent-points (1 in the file means
  // 1%). Both then store + display as percent-points ("1%").
  numericSubtypes: {
    number: 'Number',
    currency: 'Currency',
    percentage: 'Percent (0.01)',
    percent_points: 'Percent (1%)',
    // Suffix marking the percent variant the field's stored max implies — a visible
    // recommendation in the Format dropdown, never an override of the user's pick.
    matchesData: '{{label}} · matches your data',
    // aria-label for the subtype Select control.
    subtypeLabel: 'Format',
  },

  // Declared date-format labels (WS-E instance #2). Keyed by the
  // `DeclaredDateFormat` union members so `t(\`dateFormats.${format}\`)` resolves.
  // The two two-number orders name the ORDER the customer's file writes dates in;
  // `iso`/`month_name` are the unambiguous families.
  dateFormats: {
    iso: 'Year first (2024-07-04)',
    mdy: 'Month / Day / Year (07/04/2024)',
    dmy: 'Day / Month / Year (04/07/2024)',
    month_name: 'Month name (Jul 2024)',
    // Suffix marking the format the column's own values PROVE — a visible
    // recommendation in the dropdown, never an override of the user's pick.
    matchesData: '{{label}} · matches your data',
    // aria-label for the date-format Select control.
    selectLabel: 'Date format',
  },

  // ── Retype modal (the shared guarded type-change flow) ──
  retype: {
    // {{label}} is the field's display label.
    title: 'Change type: {{label}}',
    currentTypeLabel: 'Current type',
    newTypeLabel: 'New type',
    // The consequence sentence shown when the chosen type differs from the current.
    consequence: '{{willNull, number}} of {{total, number}} values will not fit and will become empty.',
    consequenceNone: 'All {{total, number}} values fit the new type. Nothing will be emptied.',
    // As-of variants used when the preview was served from the precomputed field
    // stats (very large fields): the counts are as of the last data refresh, not live.
    consequenceFromStats:
      'Based on the last data refresh, {{willNull, number}} of {{total, number}} values will not fit and will become empty.',
    consequenceNoneFromStats:
      'Based on the last data refresh, all {{total, number}} values fit the new type. Nothing will be emptied.',
    sampleLabel: 'Examples that will not fit',
    // Shown when the recompute queue is still draining, so the counts may move.
    recomputeInProgress: 'A recompute is in progress, so these counts may still shift.',
    // The honest, present-tense warning: a type change applies to all uploaded data.
    allUploadsWarning: 'Changing the type changes it for all uploaded data for this field.',
    // A subtype-only change that does NOT cross the percent boundary is display-only:
    // no scary consequence, just a light note.
    subtypeOnlyNote: 'This changes how values are displayed. It does not re-read any data.',
    // A percent-variant change (into/out of/between the fraction and percent-points
    // variants) is DATA-AFFECTING now: it changes the stored value and re-reads every
    // loan carrying this field. Warn, don't reassure.
    percentSubtypeNote:
      'Switching the percent format changes the stored values for this field and re-reads all uploaded data. Curves and slices that use it will update.',
    // F126 — the consequence preview fetch FAILED. We no longer swallow it and leave Save
    // enabled; say the impact couldn't be checked and block the save until it can.
    previewError:
      'Couldn’t check how this change affects your stored values. Save is disabled until the impact can be confirmed — try again in a moment.',
    previewErrorBatch:
      'Couldn’t check the impact of one or more type changes. Confirm is disabled until every change’s impact can be confirmed — try again in a moment.',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving…',
    saveError: 'Could not change the type. Please try again.',
  },

  // ── Custom Attributes management screen (built later; keys defined now) ──
  // The "Fields" page wraps the custom-field management UI (below) plus a
  // read-only "Standard Fields" reference in a tabbed layout.
  fields: {
    navLabel: 'Fields',
    title: 'Fields',
    subtitle: 'The fields in your portfolio: your own custom fields and the built-in standard fields.',
    tabs: {
      custom: 'Custom Fields',
      standard: 'Standard Fields',
    },
    standard: {
      // Reference + editor for the built-in fields the product understands.
      intro:
        'These are the built-in fields the product recognizes when you upload data. Their type is standard, but you control how we READ two things: what each status/flag value means, and the percent format of a rate field. Map your columns to these fields during an upload.',
      columns: {
        field: 'Field',
        type: 'Type',
        scope: 'Appears on',
        description: 'Description',
      },
      // Comma-joined list of the file types a standard field appears on.
      scope: {
        origination: 'Origination',
        snapshot: 'Snapshot',
        transaction: 'Transaction',
      },
      // Section headings within the editable Standard tab.
      sections: {
        vocabularyTitle: 'What your values mean',
        vocabularyIntro:
          'You have told us what these status and flag values mean. Changing one re-reads your data — the loss and prepayment curves update to match.',
        vocabularyEmpty:
          'No status or flag values are classified yet. Values are classified when you map a status or flag column during an upload.',
        percentTitle: 'Rate format',
        percentIntro:
          'How a rate column is written in your files. Changing it re-reads the stored values and updates any modeling that uses the rate.',
        catalogTitle: 'All standard fields',
      },
      vocabulary: {
        // Column headers for the per-value classification editor.
        value: 'Value',
        meaning: 'Meaning',
        // The classification family a field belongs to (drives the vocabulary options).
        familyStatus: 'Status',
        familyFlag: 'Flag',
        // The count of distinct classified values under a field.
        valueCount_one: '{{count, number}} value',
        valueCount_other: '{{count, number}} values',
      },
      // Sticky unsaved banner (mirrors the Custom Fields tab).
      unsavedBannerTitle: 'Unsaved changes',
      unsavedBanner_one: '{{count, number}} change is not saved yet.',
      unsavedBanner_other: '{{count, number}} changes are not saved yet.',
      discard: 'Discard',
      save: 'Save changes',
      saving: 'Saving…',
      saved: 'Your changes are saved.',
      saveError: 'Could not save your changes. Please try again.',
      loadError: 'Could not load your standard fields.',
      // Warning shown when a pending change re-reads uploaded data.
      recomputeNote: 'Saving re-reads your uploaded data, so curves and slices will update.',
    },
  },
  customAttributes: {
    title: 'Custom Fields',
    subtitle: 'The custom fields in your portfolio, and the type each is stored as.',
    columns: {
      field: 'Field',
      type: 'Type',
      coverage: 'Coverage',
      sample: 'Sample values',
    },
    // {{count}} loans carry a value for the field.
    coverageLoans_one: '{{count, number}} loan',
    coverageLoans_other: '{{count, number}} loans',
    emptyTitle: 'No custom attributes yet',
    emptyBody:
      'Custom attributes appear here once you upload columns that are kept as custom fields.',
    changedIndicator: 'Changed',
    // Unsaved-changes banner shown while one or more rows have a pending type change.
    unsavedBannerTitle: 'Unsaved changes',
    unsavedBanner_one: '{{count, number}} field has an unsaved type change.',
    unsavedBanner_other: '{{count, number}} fields have unsaved type changes.',
    discard: 'Discard changes',
    save: 'Save changes',
    saving: 'Saving…',
    saveError: 'Could not save your changes. Please try again.',
    saved: 'Changes saved.',
    loadError: 'Could not load your custom attributes. Please try again.',
    // The dimmed "no sample values" placeholder in the table sample column.
    noSamples: 'No sample values',
    // {{count}} distinct non-blank values across the portfolio.
    distinctValues_one: '{{count, number}} distinct value',
    distinctValues_other: '{{count, number}} distinct values',
    // The numeric/date range line, e.g. "Range: 300 to 850".
    range: 'Range: {{min}} to {{max}}',
    // Save-confirmation modal (changing a type re-reads uploaded data).
    confirmTitle: 'Save type changes?',
    confirmBody:
      'Changing a field type changes it for all uploaded data for that field. Values that do not fit the new type become empty.',
    // The "old type to new type" line for each changed field in the confirm modal.
    confirmChange: '{{from}} to {{to}}',
    // A display-only subtype row (number↔currency) in the confirm modal needs no
    // consequence preview.
    confirmSubtypeOnly: 'Display format only. No data is re-read.',
    // A percent-variant change is data-affecting: it re-scales the stored values.
    confirmPercentSubtype: 'Changes the stored percent values and re-reads all uploaded data for this field.',
    confirmSave: 'Save changes',
    confirmCancel: 'Keep editing',
  },

  // ── Public parsing-rules page (how values are parsed by type) ──
  parsingRules: {
    title: 'How your data is parsed',
    intro:
      'Each field is stored as a type, and that type decides how its values are read. This page explains how numeric, date, categorical, and text values are parsed.',
    // The worked-examples table below the rules.
    examplesTitle: 'Worked examples',
    examplesIntro:
      'Each row shows a value as it might appear in your file and the number it becomes. Values that are not recognized as numbers are kept as text.',
    columns: {
      input: 'Input',
      result: 'Result',
      note: 'Note',
    },
    // Shown in the Result column when a value is not parsed as a number.
    keptAsText: 'Kept as text',
    // The date section (rules + worked examples for the date grammar).
    dates: {
      title: 'How dates are read',
      intro:
        'Date fields work the same way as numbers: one consistent rule, applied the same everywhere. Formats that can only mean one thing are always accepted; a date written as two numbers is read using the Day/Month order declared for that column — suggested from your data when it can be proven, otherwise the US Month/Day order, shown with the column so you can correct it.',
      examplesTitle: 'Worked date examples',
      examplesIntro:
        'Each row shows a value as it might appear in your file, the declared order it is read under, and the date it becomes. Values that are not recognized as dates are kept as text.',
      columns: {
        format: 'Declared order',
      },
      // Labels for the declared per-column date format values.
      formats: {
        iso: 'Year first',
        mdy: 'Month/Day',
        dmy: 'Day/Month',
        month_name: 'Month name',
      },
    },
  },

  // Fixed labels baked into the kit chart primitives (ModelingCurveChart /
  // ModelingColumnChart in components/ui/charts). Domain-specific series names,
  // axis titles, and value formatters are passed in by the calling screen; only
  // these built-in chrome strings live here.
  charts: {
    // "{months} mo ({years} yr)" — age axis tooltip header.
    age: '{{months, number}} mo ({{years}} yr)',
    // The dashed tail of a curve (extrapolated past observed data) — e.g.
    // "Cumulative loss (projected)".
    projectedSuffix: '{{label}} (projected)',
    // F180: the shaded band over the OBSERVED region is a Greenwood/binomial interval
    // at z = 1.96 — a ~95% confidence interval. Named as such so it is never read as
    // more (or less) than a 95% statistical interval.
    band95: '~95% interval',
    // F180: the shaded region PAST the observed data is a heuristic projection fan
    // (half-width inflated with age), NOT a statistical interval — labeled distinctly
    // so the two are never read as the same statistic.
    bandProjected: 'Projected range',
    // "{lower} to {upper}" — band range value.
    bandRange: '{{lower}} to {{upper}}',
    // Loans backing a given age (the faint exposure bars).
    exposure: 'Exposure n',
    // The break-even reference marker on the cash-flow column chart.
    breakEven: 'Break-even',
    // "Month {{month}}" — column-chart tooltip header.
    month: 'Month {{month, number}}',
  },

  // The Portfolio Segment Builder: the panel plus its three kit filter controls
  // (RangeControl / ValueChecklist / TextFilterControl). The readiness chip labels
  // deliberately reuse `portfolio.filters.*` (one string per concept).
  segment: {
    panel: {
      label: 'Segment filters',
      labelCollapsed: 'Segment filters (collapsed)',
      expand: 'Expand segment filters',
      collapse: 'Collapse segment filters',
      // Collapsed-rail badge aria label, pluralized by active filter count.
      activeFilters_one: '{{count, number}} active filter',
      activeFilters_other: '{{count, number}} active filters',
      loading: 'Loading…',
      // Header title shown when the panel drives no live count (Modeling), instead
      // of a perpetual "Loading…".
      title: 'Segment',
      // The headline: "1,204 of 5,310 loans".
      countOf: '{{matched, number}} of {{total, number}} loans',
      updating: 'updating…',
      // Count failure states: keep the last good number, say it could not update.
      countUnavailable: 'Count unavailable',
      countError: 'Could not update the count.',
      countTooLarge: 'This segment is too complex to count. Narrow it and retry.',
      retry: 'Retry',
      reset: 'Reset',
      searchPlaceholder: 'Search fields…',
      searchLabel: 'Search fields',
      noFields: 'No fields yet. Upload a portfolio to start segmenting.',
      noMatches: 'No fields match “{{search}}”.',
      unavailableTitle: 'Unavailable Fields',
      unavailableHint: 'No data uploaded for these yet.',
      // Field search grouping: matches on top, then any active filters the search would
      // otherwise hide, so a slice never silently disappears from view while searching.
      searchResultsTitle: 'Search Results',
      enabledFieldsTitle: 'Enabled Fields',
      enabledFieldsHint: 'Active filters your search does not match.',
      // Field header subtitle: how many loans in the WHOLE portfolio carry a value for
      // this field (its data coverage). Deliberately independent of the active segment.
      covered: '{{count, number}} loans have a value for this field',
      // F101 — honest semantics for an observation-level (per-snapshot) field: the filter matches
      // the loan's most recent REPORTED value; a later blank snapshot does not clear an earlier one.
      observationValueHint:
        'Matches the most recent reported value — a later blank snapshot does not clear it.',
      filterActive: 'Filter active',
      clearFilter: 'Clear {{label}} filter',
      readiness: 'Readiness',
      noValues: 'No values to filter.',
    },
    // RangeControl (numeric/date multi-range rows, each range- or exact-valued).
    range: {
      row: 'Range',
      rowN: 'Range {{index, number}}',
      removeRow: 'Remove range {{index, number}}',
      addRange: 'Add range',
      includeMissing: 'Include missing',
      // The per-row range/exact mode toggle.
      modeLabel: '{{label}} range {{index, number}} mode',
      modeRange: 'Range',
      modeExact: 'Exact',
      exactValue: '{{label}} exact value',
      sliderLabel: '{{label}} range {{index, number}}',
      minimum: '{{label}} minimum',
      maximum: '{{label}} maximum',
      // Typed From/To date-field labels + the calendar (month-picker) button label.
      from: 'From',
      to: 'To',
      openCalendar: 'Choose a month for {{label}}',
    },
    // ValueChecklist (categorical inclusion checklist).
    checklist: {
      searchPlaceholder: 'Search {{label}}…',
      searchLabel: 'Search {{label}}',
      selectAll: 'Select all',
      selectNone: 'Select none',
      selectedCount: '{{count, number}} selected',
      noMatches: 'No matching values',
      // Search grouping: matches on top, then any checked values the search hides.
      searchResults: 'Search Results',
      enabledValues: 'Enabled Values',
      includeBlank: 'Include blank / missing',
      // F122 — truncation disclosure + server search for a >200-distinct field.
      truncated: 'Showing top {{shown, number}} of {{total}} values — search to find the rest.',
      searching: 'Searching all values…',
      moreMatches: 'More matches exist — refine your search to see them.',
      selectAllNarrow: 'Too many values to select at once — narrow your search first.',
    },
    // TextFilterControl (free-text operators).
    text: {
      operatorLabel: '{{label}} operator',
      valueLabel: '{{label}} value',
      valuePlaceholder: 'Value',
      regexPlaceholder: 'e.g. ^ACME-\\d+$',
      caseSensitive: 'Case sensitive',
      regexTooLong: 'Pattern is too long (max {{max, number}} characters).',
      regexInvalid: 'Invalid regular expression: {{detail}}',
      // F100: valid in JavaScript but not in the Postgres POSIX-ARE engine that runs the filter.
      regexUnsupported:
        'This pattern uses a feature the filter engine doesn’t support (named groups, backreferences, or \\p{…}). Use basic POSIX regex syntax.',
      ops: {
        contains: 'Contains',
        equals: 'Equals',
        startsWith: 'Starts with',
        endsWith: 'Ends with',
        notContains: 'Does not contain',
        regex: 'Matches regex',
        isBlank: 'Is blank',
        isNotBlank: 'Is not blank',
      },
    },
  },

  common: {
    loading: 'Loading...',
    cancel: 'Cancel',
    submit: 'Submit',
    close: 'Close',
    next: 'Next',
  },
  greeting: 'Hello, {{name}}!',
} as const;

export default en;
